import React from 'react'

// NBA team ID mapping for logo URLs
export const NBA_TEAM_IDS = {
  'ATL': 1610612737, 'BOS': 1610612738, 'BKN': 1610612751, 'CHA': 1610612766,
  'CHI': 1610612741, 'CLE': 1610612739, 'DAL': 1610612742, 'DEN': 1610612743,
  'DET': 1610612765, 'GSW': 1610612744, 'HOU': 1610612745, 'IND': 1610612754,
  'LAC': 1610612746, 'LAL': 1610612747, 'MEM': 1610612763, 'MIA': 1610612748,
  'MIL': 1610612749, 'MIN': 1610612750, 'NOP': 1610612740, 'NYK': 1610612752,
  'OKC': 1610612760, 'ORL': 1610612753, 'PHI': 1610612755, 'PHX': 1610612756,
  'POR': 1610612757, 'SAC': 1610612758, 'SAS': 1610612759, 'TOR': 1610612761,
  'UTA': 1610612762, 'WAS': 1610612764,
  // Alternate names
  'Hawks': 1610612737, 'Celtics': 1610612738, 'Nets': 1610612751, 'Hornets': 1610612766,
  'Bulls': 1610612741, 'Cavaliers': 1610612739, 'Mavericks': 1610612742, 'Nuggets': 1610612743,
  'Pistons': 1610612765, 'Warriors': 1610612744, 'Rockets': 1610612745, 'Pacers': 1610612754,
  'Clippers': 1610612746, 'Lakers': 1610612747, 'Grizzlies': 1610612763, 'Heat': 1610612748,
  'Bucks': 1610612749, 'Timberwolves': 1610612750, 'Pelicans': 1610612740, 'Knicks': 1610612752,
  'Thunder': 1610612760, 'Magic': 1610612753, '76ers': 1610612755, 'Suns': 1610612756,
  'Trail Blazers': 1610612757, 'Blazers': 1610612757, 'Kings': 1610612758, 'Spurs': 1610612759, 
  'Raptors': 1610612761, 'Jazz': 1610612762, 'Wizards': 1610612764,
}

// NFL team abbreviations for ESPN logo URLs
export const NFL_TEAMS = {
  'ARI': 'ari', 'ATL': 'atl', 'BAL': 'bal', 'BUF': 'buf',
  'CAR': 'car', 'CHI': 'chi', 'CIN': 'cin', 'CLE': 'cle',
  'DAL': 'dal', 'DEN': 'den', 'DET': 'det', 'GB': 'gb',
  'HOU': 'hou', 'IND': 'ind', 'JAX': 'jax', 'KC': 'kc',
  'LV': 'lv', 'LAC': 'lac', 'LAR': 'lar', 'MIA': 'mia',
  'MIN': 'min', 'NE': 'ne', 'NO': 'no', 'NYG': 'nyg',
  'NYJ': 'nyj', 'PHI': 'phi', 'PIT': 'pit', 'SF': 'sf',
  'SEA': 'sea', 'TB': 'tb', 'TEN': 'ten', 'WSH': 'wsh',
  // Alternate names
  'Cardinals': 'ari', 'Falcons': 'atl', 'Ravens': 'bal', 'Bills': 'buf',
  'Panthers': 'car', 'Bears': 'chi', 'Bengals': 'cin', 'Browns': 'cle',
  'Cowboys': 'dal', 'Broncos': 'den', 'Lions': 'det', 'Packers': 'gb',
  'Texans': 'hou', 'Colts': 'ind', 'Jaguars': 'jax', 'Chiefs': 'kc',
  'Raiders': 'lv', 'Chargers': 'lac', 'Rams': 'lar', 'Dolphins': 'mia',
  'Vikings': 'min', 'Patriots': 'ne', 'Saints': 'no', 'Giants': 'nyg',
  'Jets': 'nyj', 'Eagles': 'phi', 'Steelers': 'pit', '49ers': 'sf',
  'Seahawks': 'sea', 'Buccaneers': 'tb', 'Titans': 'ten', 'Commanders': 'wsh',
}

// Team logo component - supports NBA and NFL
export default function TeamLogo({ name, code, teamId, league, size = 28, className = '' }) {
  // Prioritize explicit league prop, then infer from team code
  // This prevents ATL (Hawks vs Falcons) and other overlapping abbreviations from mismatching
  const leagueLower = league?.toLowerCase()
  
  let logoUrl = null
  
  if (leagueLower === 'nfl') {
    // Explicit NFL - use ESPN CDN
    const nflCode = NFL_TEAMS[code] || NFL_TEAMS[name]
    if (nflCode) {
      logoUrl = `https://a.espncdn.com/i/teamlogos/nfl/500/${nflCode}.png`
    }
  } else if (leagueLower === 'nba' || !league) {
    // Explicit NBA or no league specified - try NBA first
    const nbaId = NBA_TEAM_IDS[code] || NBA_TEAM_IDS[name] || teamId
    if (nbaId) {
      logoUrl = `https://cdn.nba.com/logos/nba/${nbaId}/primary/L/logo.svg`
    } else if (!league) {
      // No league and not NBA - try NFL as fallback
      const nflCode = NFL_TEAMS[code] || NFL_TEAMS[name]
      if (nflCode) {
        logoUrl = `https://a.espncdn.com/i/teamlogos/nfl/500/${nflCode}.png`
      }
    }
  }
  
  // Fallback to initial if no logo
  if (!logoUrl) {
    const initial = (code || name || 'T')[0].toUpperCase()
    return (
      <div 
        className={`flex items-center justify-center rounded-full text-white font-bold ${className}`}
        style={{ 
          width: `${size}px`, 
          height: `${size}px`, 
          backgroundColor: '#6B7280',
          fontSize: `${Math.max(10, size * 0.4)}px`
        }}
      >
        {initial}
      </div>
    )
  }
  
  return (
    <img 
      src={logoUrl}
      alt={name || code}
      className={`object-contain ${className}`}
      style={{ 
        width: `${size}px`, 
        height: `${size}px`
      }}
      onError={(e) => {
        // Replace with fallback initial
        const initial = (code || name || 'T')[0].toUpperCase()
        const parent = e.target.parentNode
        const fallback = document.createElement('div')
        fallback.className = `flex items-center justify-center rounded-full text-white font-bold ${className}`
        fallback.style.cssText = `width: ${size}px; height: ${size}px; background-color: #6B7280; font-size: ${Math.max(10, size * 0.4)}px;`
        fallback.textContent = initial
        parent.replaceChild(fallback, e.target)
      }}
    />
  )
}
